import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { strCode } from "../../../../consts";
import { deviceVersionPatch } from "../../consts/deviceVersionPatch";
import { t } from "../../../../services";
import { fwuVersionsUtil } from "../../../../utils";

import { FlexContainers, Paragraph2, TooltipIcon, WrapperContainer } from "../../../../components";
import SelectedItemsList from "../../../../components/DropdownMultiselectInput/SelectedItemsList";
import { VersionInput } from "../VersionInput/VersionInput";
import { InfoText, TitleWrapper } from "./styled";

export const CurrentFirmwareVersionInput = ({
  versions = [],
  setVersions = () => {},
  viewOnlyOptionIds = [],
  selectedDevice,
  maxVersion,
  disabled,
  handleSelectPatch = () => {},
  isOptional,
  hasError
}) => {
  const onAddVersion = (version) => {
    if (!versions.find((v) => v.id === version)) {
      setVersions((prev) => [...prev, { id: version, value: version }]);
    }
  };

  const patchVersion = useMemo(() => {
    if (selectedDevice) {
      return deviceVersionPatch[selectedDevice] ?? null;
    }

    return null;
  }, [selectedDevice]);

  useEffect(() => {
    if (patchVersion !== null) {
      const filteredVersions = versions.filter((v) => {
        const path = fwuVersionsUtil.splitVersion(v.value)[2];

        return patchVersion === path;
      });

      setVersions(filteredVersions);
    }
  }, [patchVersion]);

  return (
    <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={16} width={"100%"}>
      <TitleWrapper Container={FlexContainers.FlexRowBetween} width={"100%"}>
        <WrapperContainer Container={FlexContainers.FlexRowStart} alignItems="center" gap={6}>
          <Paragraph2>{t(strCode.CURRENT_FW_VERSION_TITLE)}</Paragraph2>

          <WrapperContainer Container={FlexContainers.FlexColumnCenter}>
            <TooltipIcon>{t(strCode.CURRENT_FW_VERSION_HINT)}</TooltipIcon>
          </WrapperContainer>
        </WrapperContainer>

        {isOptional && <InfoText>{t(strCode.OPTIONAL_INFO)}</InfoText>}
      </TitleWrapper>
      <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={16} width={"100%"}>
        {versions?.length ? (
          <SelectedItemsList
            items={versions}
            onDelete={(id) => setVersions((prev) => prev.filter((ver) => ver.id !== id))}
            checkIsOptionDisabled={(id) => disabled || !!(viewOnlyOptionIds ?? []).includes(id)}
          />
        ) : null}

        <VersionInput
          versions={versions}
          maxVersion={maxVersion}
          onAddVersion={onAddVersion}
          disabled={disabled}
          patchVersion={patchVersion}
          handleSelectPatch={handleSelectPatch}
          hasError={hasError}
        />
      </WrapperContainer>
    </WrapperContainer>
  );
};

CurrentFirmwareVersionInput.propTypes = {
  versions: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
      .isRequired
  ),
  setVersions: PropTypes.func.isRequired,
  viewOnlyOptionIds: PropTypes.arrayOf(PropTypes.string),
  selectedDevice: PropTypes.string,
  maxVersion: PropTypes.string,
  disabled: PropTypes.bool,
  handleSelectPatch: PropTypes.func,
  isOptional: PropTypes.bool,
  hasError: PropTypes.bool
};
